import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiPost } from '../lib/api';
import { useAuth } from '../context/AuthContext';

const OPTIONS = [
  { nb_min: 4, label: '1 heure (4 × 15 min)' },
  { nb_min: 16, label: '4 heures (16 × 15 min)' },
  { nb_min: 32, label: '8 heures (32 × 15 min)' },
  { nb_min: 48, label: '12 heures (48 × 15 min)' },
] as const;

interface InitResponse {
  url: string;
  fields: Record<string, string>;
  preview: { total_eur: number; nb_min: number; ref: string };
}

export function RechargeCreditPage() {
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const formRef = useRef<HTMLFormElement | null>(null);
  const [pendingForm, setPendingForm] = useState<InitResponse | null>(null);

  useEffect(() => {
    if (pendingForm && formRef.current) {
      formRef.current.submit();
    }
  }, [pendingForm]);

  async function startPayment(nbMin: number) {
    setSubmitting(true);
    setErr(null);
    try {
      const res = await apiPost<InitResponse>('/api/credit/init', { nb_min: nbMin });
      setPendingForm(res);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Erreur');
      setSubmitting(false);
    }
  }

  const status = searchParams.get('status');

  return (
    <div style={{ maxWidth: 640 }}>
      <div style={{ marginBottom: 12 }}>
        <Link to="/" style={{ fontSize: 'var(--fs-xs)' }}>
          ← retour
        </Link>
      </div>
      <h1 style={{ fontSize: 'var(--fs-xl)', fontWeight: 600, marginBottom: 16 }}>💳 Recharger le crédit temps</h1>

      {status === 'ok' && (
        <div style={banner('var(--success-soft)', 'var(--success-text)')}>
          ✓ Paiement confirmé. Votre crédit temps sera mis à jour dans quelques secondes.
        </div>
      )}
      {status === 'ko' && (
        <div style={banner('var(--danger-soft)', 'var(--danger-text)')}>✗ Paiement refusé ou annulé. Vous pouvez réessayer ci-dessous.</div>
      )}

      {!user?.is_admin && (
        <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-mute)', marginBottom: 16 }}>
          La recharge est disponible pour les clients en mode TMA crédit. Le tarif unitaire dépend de votre contrat
          (montant_credit_temps / 4 = prix de la tranche de 15 minutes).
        </p>
      )}

      <div style={{ display: 'grid', gap: 12 }}>
        {OPTIONS.map((opt) => (
          <button
            key={opt.nb_min}
            type="button"
            disabled={submitting}
            onClick={() => startPayment(opt.nb_min)}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '14px 16px',
              background: 'var(--bg)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              cursor: submitting ? 'wait' : 'pointer',
              fontSize: 'var(--fs-base)',
              textAlign: 'left',
            }}
          >
            <div>
              <div style={{ fontWeight: 600 }}>{opt.label}</div>
              <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-mute)' }}>
                {opt.nb_min} tranches × 15 minutes = {opt.nb_min * 15} minutes
              </div>
            </div>
            <div style={{ color: 'var(--accent)' }}>→ Payer par CB</div>
          </button>
        ))}
      </div>

      {err && (
        <div style={{ ...banner('var(--danger-soft)', 'var(--danger-text)'), marginTop: 14 }}>{err}</div>
      )}

      {/* Form auto-submit caché pour rediriger vers Paybox */}
      {pendingForm && (
        <form ref={formRef} action={pendingForm.url} method="POST" style={{ display: 'none' }}>
          {Object.entries(pendingForm.fields).map(([k, v]) => (
            <input key={k} type="hidden" name={k} value={v} readOnly />
          ))}
        </form>
      )}

      {pendingForm && (
        <div style={{ marginTop: 16, color: 'var(--text-mute)', fontSize: 'var(--fs-sm)' }}>
          Redirection vers Paybox… Vous allez régler {pendingForm.preview.total_eur.toFixed(2)} € TTC.
        </div>
      )}
    </div>
  );
}

function banner(bg: string, fg: string): React.CSSProperties {
  return {
    padding: '10px 14px',
    background: bg,
    color: fg,
    borderRadius: 6,
    fontSize: 'var(--fs-sm)',
    marginBottom: 14,
  };
}
